import { Building2, FileText, History, Palette, RefreshCw, Sparkles } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, NavLink, useMatch } from 'react-router';
import { PAINT_SPEC_REPORT, REPORT_PARAM, paths } from '@/app/paths';
import { useGenerationStats } from '@/features/history';
import { useStudio } from '@/features/studio';
import { cn } from '@/lib/cn';

function NavTab({
  to,
  icon,
  label,
  badge,
  id,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  badge?: number;
  id: string;
}) {
  return (
    <NavLink
      to={to}
      end
      id={id}
      className={({ isActive }) =>
        cn(
          'px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5',
          isActive ? 'bg-white text-blue-600 shadow-xs' : 'text-slate-600 hover:text-slate-900',
        )
      }
    >
      {({ isActive }) => (
        <>
          {icon}
          <span className="hidden sm:inline">{label}</span>
          {!!badge && (
            <span
              className={cn(
                'px-1.5 py-0.2 rounded-full text-[10px] font-mono font-bold',
                isActive ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-700',
              )}
            >
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

export function AppHeader() {
  const { state, reset } = useStudio();
  const { data: stats } = useGenerationStats();
  const onStudio = useMatch(paths.studio) !== null;

  return (
    <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-40 shadow-sm flex items-center justify-between px-4 sm:px-8">
      <Link to={paths.studio} className="flex items-center gap-3">
        <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center text-white font-bold shadow-md shadow-blue-600/20">
          <Building2 className="w-5 h-5" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="text-base sm:text-lg font-bold tracking-tight text-slate-800 font-sans">
              Indian Exterior Makeover
            </span>
            <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-[10px] font-bold uppercase tracking-wider">
              <Sparkles className="w-3 h-3 text-blue-600" /> Sleek AI
            </span>
          </div>
          <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold leading-none">
            Architectural Rendering & Paint Engine
          </p>
        </div>
      </Link>

      <nav aria-label="Main" className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200">
        <NavTab to={paths.studio} id="tab-studio-btn" icon={<Palette className="w-3.5 h-3.5" />} label="Studio" />
        <NavTab
          to={paths.history}
          id="tab-history-btn"
          icon={<History className="w-3.5 h-3.5" />}
          label="History"
          badge={stats?.count}
        />
      </nav>

      <div className="flex items-center gap-2">
        {onStudio && state.result && (
          <Link
            to={{ pathname: paths.studio, search: `?${REPORT_PARAM}=${PAINT_SPEC_REPORT}` }}
            className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-semibold rounded-lg border border-slate-200 transition-all flex items-center gap-1.5 shadow-xs"
            id="header-paint-spec-btn"
          >
            <FileText className="w-3.5 h-3.5 text-blue-600" />
            <span className="hidden sm:inline">Paint Spec Report</span>
            <span className="sm:hidden">Report</span>
          </Link>
        )}
        <button
          type="button"
          onClick={reset}
          className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-lg transition-all flex items-center gap-1.5 shadow-sm"
          id="header-reset-btn"
          title="Reset form to defaults"
        >
          <RefreshCw className="w-3.5 h-3.5 text-slate-300" />
          <span className="hidden sm:inline">Reset</span>
        </button>
      </div>
    </header>
  );
}
